import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ReactCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ImageCropModal.css';

const ImageCropModal = ({ isOpen, onClose, onCropComplete, imageSrc }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 60,
    height: 60,
    x: 20,
    y: 20,
    aspect: 1
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const onLoad = useCallback((e) => {
    const img = e.target;
    imgRef.current = img;
    setImageLoaded(true);
    
    // Calculate proper square crop based on image dimensions
    if (img.naturalWidth && img.naturalHeight) {
      // a. Check the size of the image and compare height and width - whichever is bigger is our baseline
      const baseline = Math.max(img.naturalWidth, img.naturalHeight);
      
      // b. Make both height and width of initial crop = baseline, so both square, perfectly within bounds
      const squareSize = Math.min(img.naturalWidth, img.naturalHeight); // Use smaller dimension to stay within bounds
      
      // Convert to percentages for ReactCrop
      const cropWidthPercent = (squareSize / img.naturalWidth) * 100;
      const cropHeightPercent = (squareSize / img.naturalHeight) * 100;
      
      // Center the crop
      const xPercent = ((img.naturalWidth - squareSize) / 2 / img.naturalWidth) * 100;
      const yPercent = ((img.naturalHeight - squareSize) / 2 / img.naturalHeight) * 100;
      
      const newCrop = {
        unit: '%',
        width: cropWidthPercent,
        height: cropHeightPercent,
        x: xPercent,
        y: yPercent,
        aspect: 1
      };
      
      setCrop(newCrop);
      setCompletedCrop(newCrop);
    }
   }, []);

  // Update preview canvas whenever crop changes
  const updatePreview = useCallback((image, crop) => {
    const canvas = canvasRef.current;
    if (!canvas || !image || !crop || !crop.width || !crop.height) {
      return;
    }

    if (!(image instanceof HTMLImageElement)) {
      setPreviewError('Invalid image format. Please try a different image.');
      return;
    }
    
    // Wait for image to load if it's not ready yet
    if (!image.complete) {
      const handleLoad = () => {
        updatePreview(image, crop);
        image.removeEventListener('load', handleLoad);
      };
      image.addEventListener('load', handleLoad);
      return;
    }
    
    if (!image.naturalWidth || !image.naturalHeight) {
      setPreviewError('Could not read image dimensions. Please try a different image.');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setPreviewError('Canvas not supported in this browser.');
      return;
    }
    try {
      // Clear any previous error on new attempt
      setPreviewError(null);
      
      // Additional validation for crop bounds
      if (crop.width <= 0 || crop.height <= 0 || crop.x < 0 || crop.y < 0) {
        return;
      }
      
      // Set canvas size for preview (fixed 150x150)
      const previewSize = 150;
      canvas.width = previewSize;
      canvas.height = previewSize;

      // Calculate crop dimensions in pixels - handle both percentage and pixel units
      let cropX, cropY, cropWidth, cropHeight;
      
      if (crop.unit === '%') {
        cropX = Math.round((crop.x / 100) * image.naturalWidth);
        cropY = Math.round((crop.y / 100) * image.naturalHeight);
        cropWidth = Math.round((crop.width / 100) * image.naturalWidth);
        cropHeight = Math.round((crop.height / 100) * image.naturalHeight);
      } else {
        // Scale from display size to natural size
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        cropX = Math.round(crop.x * scaleX);
        cropY = Math.round(crop.y * scaleY);
        cropWidth = Math.round(crop.width * scaleX);
        cropHeight = Math.round(crop.height * scaleY);
      }

      // Clamp to image bounds
      cropX = Math.max(0, Math.min(cropX, image.naturalWidth - 1));
      cropY = Math.max(0, Math.min(cropY, image.naturalHeight - 1));
      cropWidth = Math.max(1, Math.min(cropWidth, image.naturalWidth - cropX));
      cropHeight = Math.max(1, Math.min(cropHeight, image.naturalHeight - cropY));

      // Final bounds validation before drawing
      if (cropX < 0 || cropY < 0 || cropWidth <= 0 || cropHeight <= 0 ||
          cropX + cropWidth > image.naturalWidth || cropY + cropHeight > image.naturalHeight) {
        ctx.clearRect(0, 0, previewSize, previewSize);
        setPreviewError('Crop area is out of bounds. Please adjust the selection.');
        return;
      }

      // Clear canvas
      ctx.clearRect(0, 0, previewSize, previewSize);

      // Draw the cropped image
      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        previewSize,
        previewSize
      );
    } catch (error) {
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setPreviewError('Failed to generate preview. You can still try to save.');
    }
  }, []);

  // Update preview when crop is completed (after dragging stops)
  useEffect(() => {
    if (imgRef.current && completedCrop && imageLoaded && completedCrop.width && completedCrop.height) {
      // Use requestAnimationFrame to prevent forced reflows
      const rafId = requestAnimationFrame(() => {
        updatePreview(imgRef.current, completedCrop);
      });
      
      return () => cancelAnimationFrame(rafId);
    }
  }, [completedCrop, updatePreview, imageLoaded]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setImageLoaded(false);
      setIsProcessing(false);
      setCompletedCrop(null);
      setPreviewError(null);
      // Reset crop to initial square state
      setCrop({
        unit: 'px',
        width: 200,
        height: 200,
        x: 50,
        y: 50,
        aspect: 1
      });
    }
  }, [isOpen]);

  const getCroppedImg = useCallback((image, crop) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!crop || !canvas || !ctx || !crop.width || !crop.height) {
      throw new Error('Canvas context or crop data not available');
    }

    if (!(image instanceof HTMLImageElement) || !image.complete || !image.naturalWidth) {
      throw new Error('Invalid or incomplete image element');
    }
    try {
      // Handle both percentage and pixel units from ReactCrop
      let cropX, cropY, cropWidth, cropHeight;
      
      if (crop.unit === '%') {
        // Convert percentage to pixels
        cropX = Math.round((crop.x / 100) * image.naturalWidth);
        cropY = Math.round((crop.y / 100) * image.naturalHeight);
        cropWidth = Math.round((crop.width / 100) * image.naturalWidth);
        cropHeight = Math.round((crop.height / 100) * image.naturalHeight);
      } else {
        // Already in pixels - but need to scale from display size to natural size
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        cropX = Math.round(crop.x * scaleX);
        cropY = Math.round(crop.y * scaleY);
        cropWidth = Math.round(crop.width * scaleX);
        cropHeight = Math.round(crop.height * scaleY);
      }
      
      // Clamp crop bounds to image dimensions
      cropX = Math.max(0, Math.min(cropX, image.naturalWidth - 1));
      cropY = Math.max(0, Math.min(cropY, image.naturalHeight - 1));
      cropWidth = Math.max(1, Math.min(cropWidth, image.naturalWidth - cropX));
      cropHeight = Math.max(1, Math.min(cropHeight, image.naturalHeight - cropY));
      
      // Optimize output size - max 400x400 for profile pictures
      const maxOutputSize = 400;
      const outputSize = Math.min(maxOutputSize, Math.max(cropWidth, cropHeight));
      
      // Set canvas size to optimized dimensions
      canvas.width = outputSize;
      canvas.height = outputSize;

      // Enable image smoothing for better quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the cropped image with optimization
      ctx.drawImage(
        image,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        outputSize,
        outputSize
      );
    } catch (error) {
      throw new Error('Failed to draw image on canvas: ' + error.message);
    }

    return new Promise((resolve, reject) => {
      // Use higher compression for smaller file sizes and faster uploads
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      }, 'image/jpeg', 0.8); // Reduced quality from 0.9 to 0.8 for smaller files
    });
  }, []);

  const handleCropSave = async () => {
    // If no completedCrop but we have a current crop, use current crop
    const cropToUse = completedCrop || crop;
    
    if (!cropToUse || !imgRef.current) {
      setErrorMessage('Please select an area to crop');
      setIsProcessing(false);
      return;
    }

    setIsProcessing(true);

    // More lenient validation for image element
    const image = imgRef.current;
    if (!image) {
      setErrorMessage('Image not found. Please try again.');
      setIsProcessing(false);
      return;
    }

    // Wait for image to load if needed
    if (!image.complete) {
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Image load timeout')), 5000);
          image.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          image.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Image failed to load'));
          };
        });
      } catch (error) {
        setErrorMessage('Failed to load image. Please try again.');
        setIsProcessing(false);
        return;
      }
    }

    if (!image.naturalWidth || !image.naturalHeight) {
      setErrorMessage('Invalid image dimensions. Please try again.');
      setIsProcessing(false);
      return;
    }

    // Validate crop bounds before processing
    if (!cropToUse.width || !cropToUse.height || 
        cropToUse.width <= 0 || cropToUse.height <= 0) {
      setErrorMessage('Please select a valid crop area.');
      setIsProcessing(false);
      return;
    }

    try {
      // Add timeout to prevent infinite processing
      const processingTimeout = setTimeout(() => {
        setIsProcessing(false);
        setErrorMessage('Processing timed out. Please try again with a smaller image.');
      }, 30000); // 30 second timeout

      const croppedImageBlob = await getCroppedImg(image, cropToUse);
      clearTimeout(processingTimeout);
      
      if (croppedImageBlob) {
        // Convert blob to base64 for preview
        const reader = new FileReader();
        reader.onload = () => {
          try {
            onCropComplete(croppedImageBlob, reader.result);
            setIsProcessing(false);
          } catch (callbackError) {
            setErrorMessage('Failed to process cropped image. Please try again.');
            setIsProcessing(false);
          }
        };
        reader.onerror = () => {
          setErrorMessage('Failed to read cropped image. Please try again.');
          setIsProcessing(false);
        };
        
        // Add timeout for file reading as well
        const readTimeout = setTimeout(() => {
          setIsProcessing(false);
          setErrorMessage('Failed to read cropped image. Please try again.');
        }, 10000);
        
        reader.onload = () => {
          clearTimeout(readTimeout);
          try {
            onCropComplete(croppedImageBlob, reader.result);
            setIsProcessing(false);
          } catch (callbackError) {
            setErrorMessage('Failed to process cropped image. Please try again.');
            setIsProcessing(false);
          }
        };
        
        reader.readAsDataURL(croppedImageBlob);
      } else {
        setErrorMessage('Failed to generate cropped image. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      setErrorMessage(`Failed to crop image: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const handleCropChange = useCallback((newCrop) => {
    setCrop(newCrop);
    
    // Immediately update preview during drag
    if (imgRef.current && newCrop && imageLoaded && newCrop.width && newCrop.height) {
      updatePreview(imgRef.current, newCrop);
    }
  }, [imageLoaded, updatePreview]);

  const handleCropComplete = useCallback((c) => {
    setCompletedCrop(c);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="crop-modal-overlay">
      <div className="crop-modal">
        <div className="crop-modal-header">
          <h3>Crop Your Profile Picture</h3>
          <button className="crop-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="crop-modal-content">
          {!imageLoaded && (
            <div className="crop-loading">
              <div className="crop-loading-spinner"></div>
              <p>Loading image...</p>
            </div>
          )}
          
          <div className="crop-container" style={{ display: imageLoaded ? 'block' : 'none' }}>
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={handleCropChange}
                onComplete={handleCropComplete}
                aspect={1}
                circularCrop={false}
                keepSelection={true}
                minWidth={80}
                minHeight={80}
                maxWidth={400}
                maxHeight={400}
                locked={isProcessing}
                disabled={isProcessing}
                ruleOfThirds={false}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  onLoad={onLoad}
                  className="crop-image"
                  style={{ 
                    maxWidth: '360px', 
                    maxHeight: '360px',
                    display: 'block',
                    objectFit: 'contain',
                    opacity: isProcessing ? 0.5 : 1
                  }}
                />
              </ReactCrop>
            )}
          </div>
          
          <div className="crop-preview-section" style={{ display: imageLoaded ? 'block' : 'none' }}>
            <h4>Preview</h4>
            <div className="crop-preview-container">
              {isProcessing && (
                <div className="crop-processing-overlay">
                  <div className="crop-loading-spinner"></div>
                </div>
              )}
              <canvas
                ref={canvasRef}
                className="crop-preview-canvas"
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            </div>
            {previewError && (
              <p className="crop-preview-error" style={{ color: '#e74c3c', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                {previewError}
              </p>
            )}
          </div>
        </div>
        
        {errorMessage && (
          <div className="crop-error-banner" style={{ 
            backgroundColor: '#fee', 
            color: '#c33', 
            padding: '12px', 
            marginTop: '12px',
            borderRadius: '4px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {errorMessage}
          </div>
        )}
        
        <div className="crop-modal-actions">
          <button 
            className="crop-cancel-btn" 
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            className="crop-save-btn" 
            onClick={handleCropSave}
            disabled={!imageLoaded || !completedCrop || isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="crop-button-spinner"></span>
                Processing...
              </>
            ) : (
              'Use This Photo'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
